'use client'

import * as React from 'react'
import {
  useInitiateVoDMultipartUploadMutation,
  useCompleteVoDMultipartUploadMutation,
} from '@web/gen/service/creativefabrica/uploads/v2'
import { useNotificationOnUploadsProcessed } from './use-social-notification'
import type { NotificationPayload } from '@repo/notifications/use-notifications'
import { UploadApp } from '@web/gen/service/creativefabrica/uploads/v2/types_pb'

const CONCURRENCY_LIMIT = 3
const NOTIFICATION_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

export const useVideoUpload = () => {
  const initiateMultipartMutation = useInitiateVoDMultipartUploadMutation()
  const completeMultipartMutation = useCompleteVoDMultipartUploadMutation()

  // Use a counter so batch uploads reflect "loading" until all are done
  const [activeUploads, setActiveUploads] = React.useState(0)
  const loading = activeUploads > 0

  const [matchedNotification, setMatchedNotification] = React.useState<NotificationPayload | null>(
    null,
  )

  const { notification } = useNotificationOnUploadsProcessed()

  // Track IDs in a Ref to ensure the useEffect always sees the latest values without re-renders
  const trackedIdsRef = React.useRef<Set<string>>(new Set())

  // Store promise resolvers for active uploads
  const pendingUploadsRef = React.useRef<
    Map<
      string,
      {
        resolve: (n: NotificationPayload) => void
        reject: (e: Error) => void
        timer: ReturnType<typeof setTimeout>
      }
    >
  >(new Map())

  const beginUpload = React.useCallback(() => {
    setActiveUploads(c => c + 1)
  }, [])

  const endUpload = React.useCallback(() => {
    setActiveUploads(c => Math.max(0, c - 1))
  }, [])

  // Listen for backend processing notifications
  React.useEffect(() => {
    if (!notification) {
      return
    }

    const uploadId = notification.social?.feedPayload?.uploadProcessed?.upload?.id
    const idStr = String(uploadId)

    if (uploadId && trackedIdsRef.current.has(idStr)) {
      setMatchedNotification(notification)

      const pending = pendingUploadsRef.current.get(idStr)
      if (pending) {
        clearTimeout(pending.timer)
        pending.resolve(notification)
        pendingUploadsRef.current.delete(idStr)
        trackedIdsRef.current.delete(idStr)
      }
    }
  }, [notification])

  const uploadChunk = async (url: string, partNumber: number, blob: Blob) => {
    const response = await fetch(url, { method: 'PUT', body: blob })

    if (!response.ok) {
      throw new Error(`Part ${partNumber} failed: ${response.status}`)
    }

    const etag = response.headers.get('ETag') || response.headers.get('etag')
    if (!etag) {
      throw new Error(`ETag missing for part ${partNumber}. Check S3 CORS configuration.`)
    }

    return {
      partNumber,
      etag: etag.replace(/"/g, ''),
    }
  }

  const uploadVideo = async (file: File): Promise<NotificationPayload> => {
    beginUpload()
    let currentUploadId: string | undefined

    try {
      // 1. Initiate
      const initiation = await initiateMultipartMutation.mutateAsync({
        fileName: file.name,
        fileSize: BigInt(file.size),
        app: UploadApp.SOCIAL,
      })

      if (!initiation?.uploadId || !initiation?.urls || !initiation?.partSize) {
        throw new Error('Failed to initiate multipart upload')
      }

      currentUploadId = String(initiation.uploadId)
      const partSize = Number(initiation.partSize)
      trackedIdsRef.current.add(currentUploadId)

      // 2. Parallel Chunking with Concurrency Control (no shared mutation across workers)
      const urls = initiation.urls
      let nextIndex = 0

      const worker = async (): Promise<{ partNumber: number; etag: string }[]> => {
        const localParts: { partNumber: number; etag: string }[] = []

        while (nextIndex < urls.length) {
          const i = nextIndex++
          const partInfo = urls[i]

          const start = (partInfo.partNumber - 1) * partSize
          const end = Math.min(start + partSize, file.size)
          const chunk = file.slice(start, end)

          const result = await uploadChunk(partInfo.url, partInfo.partNumber, chunk)
          localParts.push(result)
        }

        return localParts
      }

      const numWorkers = Math.min(CONCURRENCY_LIMIT, urls.length)
      const partsByWorker = await Promise.all(Array.from({ length: numWorkers }, () => worker()))

      const completedParts = partsByWorker.flat().toSorted((a, b) => a.partNumber - b.partNumber)

      // 3. Complete Multipart Upload
      await completeMultipartMutation.mutateAsync({
        uploadId: currentUploadId,
        s3UploadId: initiation.s3UploadId,
        key: initiation.key,
        parts: completedParts,
      })

      // 4. Wait for processing notification (transcoding)
      const uploadIdForPromise = currentUploadId

      // oxlint-disable-next-line promise/avoid-new
      return await new Promise<NotificationPayload>((resolve, reject) => {
        const timer = setTimeout(() => {
          pendingUploadsRef.current.delete(uploadIdForPromise)
          trackedIdsRef.current.delete(uploadIdForPromise)
          reject(new Error('Processing timeout'))
        }, NOTIFICATION_TIMEOUT_MS)

        pendingUploadsRef.current.set(uploadIdForPromise, { resolve, reject, timer })
      })
    } catch (error) {
      if (currentUploadId) {
        pendingUploadsRef.current.delete(currentUploadId)
        trackedIdsRef.current.delete(currentUploadId)
      }
      throw error
    } finally {
      endUpload()
    }
  }

  const uploadVideos = async (files: File[]): Promise<NotificationPayload[]> => {
    // No need to set loading here — counter handles it correctly per file
    const results = await Promise.allSettled(files.map(file => uploadVideo(file)))
    return results
      .filter((r): r is PromiseFulfilledResult<NotificationPayload> => r.status === 'fulfilled')
      .map(r => r.value)
  }

  return {
    uploadVideo,
    uploadVideos,
    loading,
    matchedNotification,
  }
}
